import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Cloud, Activity, DollarSign, Clock, CheckCircle, AlertCircle, 
  TrendingUp, Zap, Globe, Users, BarChart3, RefreshCw, Eye, Settings
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
         BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';

const ExternalRAGStats = () => {
  const [agents, setAgents] = useState([]);
  const [stats, setStats] = useState([]);
  const [providerSummary, setProviderSummary] = useState({});
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentRequests, setAgentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchAllData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const fetchAllData = async () => {
    try {
      const [agentsRes, statsRes, providersRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/external-rag-agents`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/external-rag-agents/stats`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/external-rag-agents/providers/summary`)
      ]);

      if (agentsRes.ok) {
        const agentData = await agentsRes.json();
        setAgents(agentData.agents || []);
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData.stats || []);
      }

      if (providersRes.ok) {
        const providersData = await providersRes.json();
        setProviderSummary(providersData.providers || {});
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching external RAG data:', error);
      // Use mock data on error
      setAgents(generateMockAgents());
      setStats(generateMockStats());
      setProviderSummary(generateMockProviderSummary());
      setLoading(false);
    }
  };

  const fetchAgentRequests = async (agentId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/external-rag-agents/${agentId}/requests?limit=50`
      );
      
      if (response.ok) {
        const data = await response.json();
        setAgentRequests(data.requests || []);
      } else {
        setAgentRequests(generateMockRequests());
      }
    } catch (error) {
      console.error('Error fetching agent requests:', error);
      setAgentRequests(generateMockRequests());
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    if (selectedAgent) {
      await fetchAgentRequests(selectedAgent.agent_id);
    }
    setRefreshing(false);
  };

  const generateMockAgents = () => [
    {
      agent_id: '1',
      name: 'AWS Bedrock Claude Agent',
      provider: 'aws_bedrock',
      status: 'active',
      created_at: '2024-01-15T10:00:00Z',
      model_name: 'anthropic.claude-3-sonnet-20240229-v1:0',
      knowledge_base_ids: ['kb1', 'kb2'],
      categories: ['Database Issue', 'Performance']
    },
    {
      agent_id: '2', 
      name: 'GCP Vertex Gemini Pro',
      provider: 'gcp_vertex',
      status: 'active',
      created_at: '2024-01-20T14:30:00Z',
      model_name: 'gemini-pro',
      knowledge_base_ids: ['kb1', 'kb3'],
      categories: ['Network Connectivity']
    },
    {
      agent_id: '3',
      name: 'Azure OpenAI GPT-4',
      provider: 'azure_openai',
      status: 'error',
      created_at: '2024-02-01T09:15:00Z',
      model_name: 'gpt-4',
      knowledge_base_ids: ['kb2'],
      categories: ['Security Incident']
    }
  ];

  const generateMockStats = () => [
    {
      agent_id: '1',
      total_requests: 1250,
      successful_requests: 1198,
      failed_requests: 52,
      avg_response_time: 0.85,
      total_tokens_used: 125000,
      cost_estimate: 15.75,
      last_24h_requests: 89,
      uptime_percentage: 98.5,
      error_rate: 0.042
    },
    {
      agent_id: '2',
      total_requests: 890,
      successful_requests: 875,
      failed_requests: 15,
      avg_response_time: 0.62,
      total_tokens_used: 98000,
      cost_estimate: 8.90,
      last_24h_requests: 67,
      uptime_percentage: 99.2,
      error_rate: 0.017
    },
    {
      agent_id: '3',
      total_requests: 245,
      successful_requests: 201,
      failed_requests: 44,
      avg_response_time: 1.24,
      total_tokens_used: 45000,
      cost_estimate: 12.30,
      last_24h_requests: 12,
      uptime_percentage: 82.1,
      error_rate: 0.18
    }
  ];

  const generateMockProviderSummary = () => ({
    aws_bedrock: {
      total_agents: 1,
      active_agents: 1,
      total_requests: 1250,
      total_cost: 15.75,
      avg_response_time: 0.85
    },
    gcp_vertex: {
      total_agents: 1,
      active_agents: 1,
      total_requests: 890,
      total_cost: 8.90,
      avg_response_time: 0.62
    },
    azure_openai: {
      total_agents: 1,
      active_agents: 0,
      total_requests: 245,
      total_cost: 12.30,
      avg_response_time: 1.24
    }
  });

  const generateMockRequests = () => Array.from({ length: 20 }, (_, i) => ({
    request_id: `req_${i + 1}`,
    query: `Sample query ${i + 1}`,
    response: `Sample response for query ${i + 1}`,
    response_time: Math.random() * 2 + 0.2,
    tokens_used: Math.floor(Math.random() * 500 + 100),
    cost: Math.random() * 0.05 + 0.001,
    status: Math.random() > 0.1 ? 'success' : 'error',
    timestamp: new Date(Date.now() - i * 3600000).toISOString()
  }));

  const getProviderIcon = (provider) => {
    switch (provider) {
      case 'aws_bedrock': return '🔶';
      case 'gcp_vertex': return '🔵';
      case 'azure_openai': return '🔷';
      case 'anthropic': return '🟣';
      case 'cohere': return '🟢';
      case 'huggingface': return '🟡';
      default: return '☁️';
    }
  };

  const getProviderName = (provider) => {
    const names = {
      aws_bedrock: 'AWS Bedrock',
      gcp_vertex: 'GCP Vertex AI',
      azure_openai: 'Azure OpenAI',
      anthropic: 'Anthropic',
      cohere: 'Cohere',
      huggingface: 'Hugging Face'
    };
    return names[provider] || provider;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'error': return 'bg-red-100 text-red-800';
      case 'configuring': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTotalStats = () => {
    return stats.reduce((acc, stat) => ({
      totalRequests: acc.totalRequests + (stat.total_requests || 0),
      totalCost: acc.totalCost + (stat.cost_estimate || 0),
      avgResponseTime: acc.avgResponseTime + (stat.avg_response_time || 0),
      avgUptime: acc.avgUptime + (stat.uptime_percentage || 0),
      count: acc.count + 1
    }), { totalRequests: 0, totalCost: 0, avgResponseTime: 0, avgUptime: 0, count: 0 });
  };

  const totalStats = getTotalStats();
  const avgResponseTime = totalStats.count > 0 ? (totalStats.avgResponseTime / totalStats.count).toFixed(2) : 0;
  const avgUptime = totalStats.count > 0 ? (totalStats.avgUptime / totalStats.count).toFixed(1) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">External RAG Agents Statistics</h1>
          <p className="text-gray-600">Monitor performance and usage of external cloud-based RAG agents</p>
        </div>
        <Button 
          onClick={handleRefresh} 
          disabled={refreshing}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Overall Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Cloud className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{agents.length}</p>
                <p className="text-sm text-gray-600">Total Agents</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Activity className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalStats.totalRequests}</p>
                <p className="text-sm text-gray-600">Total Requests</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">${totalStats.totalCost.toFixed(2)}</p>
                <p className="text-sm text-gray-600">Total Cost</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{avgResponseTime}s</p>
                <p className="text-sm text-gray-600">Avg Response Time</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Provider Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Provider Performance Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.entries(providerSummary).map(([provider, data]) => (
              <Card key={provider} className="bg-gradient-to-r from-gray-50 to-gray-100">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">{getProviderIcon(provider)}</span>
                    <div>
                      <h3 className="font-semibold">{getProviderName(provider)}</h3>
                      <p className="text-sm text-gray-600">{data.active_agents}/{data.total_agents} active</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-gray-600">Requests</p>
                      <p className="font-medium">{data.total_requests}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Cost</p>
                      <p className="font-medium">${data.total_cost?.toFixed(2)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Agent List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            External RAG Agents ({agents.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {agents.map((agent) => {
              const agentStats = stats.find(s => s.agent_id === agent.agent_id);
              
              return (
                <div 
                  key={agent.agent_id} 
                  className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    setSelectedAgent(agent);
                    fetchAgentRequests(agent.agent_id);
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-xl">{getProviderIcon(agent.provider)}</span>
                        <div>
                          <h3 className="font-semibold">{agent.name}</h3>
                          <p className="text-sm text-gray-600">{getProviderName(agent.provider)} • {agent.model_name}</p>
                        </div>
                        <Badge className={getStatusColor(agent.status)}>
                          {agent.status}
                        </Badge>
                      </div>
                      
                      {agentStats && (
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-3 text-sm">
                          <div>
                            <p className="text-gray-600">Requests (24h)</p>
                            <p className="font-medium">{agentStats.last_24h_requests}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Success Rate</p>
                            <p className="font-medium text-green-600">
                              {((1 - agentStats.error_rate) * 100).toFixed(1)}%
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Avg Response</p>
                            <p className="font-medium">{agentStats.avg_response_time?.toFixed(2)}s</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Cost Today</p>
                            <p className="font-medium">${(agentStats.cost_estimate * 0.1).toFixed(2)}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">Uptime</p>
                            <p className="font-medium text-blue-600">
                              {agentStats.uptime_percentage?.toFixed(1)}%
                            </p>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex flex-wrap gap-1 mt-3">
                        {agent.categories?.map((category, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {category}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    <Button size="sm" variant="outline">
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Agent Detail Modal */}
      {selectedAgent && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Agent Details: {selectedAgent.name}
            </CardTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setSelectedAgent(null)}
              className="ml-auto"
            >
              Close
            </Button>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Agent Stats Chart */}
              <div>
                <h4 className="font-medium mb-4">Request Timeline (Last 20 requests)</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={agentRequests.slice(0, 20).reverse()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="timestamp" 
                      tickFormatter={(time) => new Date(time).toLocaleTimeString()}
                    />
                    <YAxis />
                    <Tooltip 
                      labelFormatter={(time) => new Date(time).toLocaleString()}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="response_time" 
                      stroke="#8884d8" 
                      strokeWidth={2}
                      name="Response Time (s)"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Recent Requests */}
              <div>
                <h4 className="font-medium mb-4">Recent Requests</h4>
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {agentRequests.slice(0, 10).map((request, index) => (
                    <div key={index} className="border rounded p-2 text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <Badge className={request.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {request.status}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {new Date(request.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-gray-700 truncate">{request.query}</p>
                      <div className="flex gap-4 text-xs text-gray-500 mt-1">
                        <span>{request.response_time?.toFixed(2)}s</span>
                        <span>{request.tokens_used} tokens</span>
                        <span>${request.cost?.toFixed(4)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExternalRAGStats;